import type {
  PaymentMethod,
  PaymentMethodConfiguration,
  PaymentMethodField,
  PaymentMethodFieldOption,
  PaymentMethodFieldType,
} from "../../../domain/entities/PaymentMethod";
import type { PaymentMethodQueryPort } from "../../../application/ports/PaymentMethodQueryPort";
import { isPaymentMobileCode } from "../../../domain/services/paymentMethodService";
import type { CountryCode } from "../../../domain/value-objects/CountryCode";
import { createCountryCode } from "../../../domain/value-objects/CountryCode";

interface PaymentsHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
}

interface PaymentMethodHttp {
  code: string;
  name: string;
  country_codes: string[];
  requires_confirmation: boolean;
  icon: string;
  transfer_info?: {
    bank_name?: string;
    account_number?: string;
    phone_number?: string;
    email?: string;
    dni?: string;
  };
  description?: string;
  configuration?: PaymentMethodConfigurationHttp;
}

interface PaymentMethodConfigurationHttp {
  fields?: PaymentMethodFieldHttp[];
  voucher?: {
    required?: boolean;
    accepted_mime_types?: string[];
    max_file_size_mb?: number;
  };
}

interface PaymentMethodFieldHttp {
  id: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helper_text?: string;
  pattern?: string;
  options?: PaymentMethodFieldOptionHttp[];
}

interface PaymentMethodFieldOptionHttp {
  label: string;
  value: string;
}

interface BffResponse<T> {
  success: boolean;
  data: T;
}

const PAYMENT_METHODS_PATH = "payments/payment-methods";
const STRIPE_METHOD_CODE = "PRW-STRIPE";
const BINANCE_CODE = "PRW-BINANCE";

export class PaymentsHttpAdapter implements PaymentMethodQueryPort {
  constructor(private readonly config: PaymentsHttpAdapterConfig) {}

  async listByCountry(countryCode: CountryCode): Promise<PaymentMethod[]> {
    const url = this.composeUrl(PAYMENT_METHODS_PATH, { country_code: countryCode });
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error("No fue posible obtener los métodos de pago");
    }

    const payload = (await response.json()) as
      | BffResponse<PaymentMethodHttp[]>
      | PaymentMethodHttp[];
    const methods = unwrapBffData(payload);

    return methods.map((method) => ({
        code: method.code,
        name: method.name,
        icon: method.icon,
        requiresConfirmation: method.requires_confirmation,
        countryCodes: method.country_codes.map((code) => createCountryCode(code)),
        description: method.description,
        transferInfo: method.transfer_info
          ? {
              bankName: method.transfer_info.bank_name,
              accountNumber: method.transfer_info.account_number,
              phoneNumber: method.transfer_info.phone_number,
              email: method.transfer_info.email,
              dni: method.transfer_info.dni,
            }
          : undefined,
        configuration: mapConfiguration(method.configuration),
        voucher: mapVoucherConfig(method.configuration, method.code),
      }));
  }

  private composeUrl(path: string, params?: Record<string, string>): string {
    const normalizedBase = this.config.baseUrl.endsWith("/") ? this.config.baseUrl : `${this.config.baseUrl}/`;
    const url = new URL(path, normalizedBase);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }

  private buildHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}

const unwrapBffData = <T,>(payload: T | BffResponse<T>): T => {
  if (isBffResponse(payload)) {
    return payload.data;
  }

  return payload;
};

const isBffResponse = <T,>(payload: T | BffResponse<T>): payload is BffResponse<T> => {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "success" in payload &&
    "data" in payload
  );
};

const mapConfiguration = (configuration?: PaymentMethodConfigurationHttp): PaymentMethodConfiguration | undefined => {
  if (!configuration?.fields?.length) {
    return undefined;
  }

  return {
    fields: configuration.fields.map(mapField),
  };
};

const mapVoucherConfig = (
  configuration: PaymentMethodConfigurationHttp | undefined,
  methodCode: string,
): PaymentMethod["voucher"] => {
  const voucher = configuration?.voucher;

  if (voucher) {
    return {
      required: Boolean(voucher.required),
      acceptedMimeTypes: voucher.accepted_mime_types,
      maxFileSizeMb: voucher.max_file_size_mb,
    };
  }

  if (isPaymentMobileCode(methodCode)) {
    return {
      required: true,
      acceptedMimeTypes: ["image/jpeg", "image/png"],
      maxFileSizeMb: 5,
    };
  }

  if (methodCode === STRIPE_METHOD_CODE) {
    return undefined;
  }

  if (methodCode === BINANCE_CODE) {
    return {
      required: false,
      acceptedMimeTypes: ["image/jpeg", "image/png", "application/pdf"],
      maxFileSizeMb: 10,
    };
  }

  if (methodCode === "PRW-BANK-TRANSFER") {
    return {
      required: false,
      acceptedMimeTypes: ["image/jpeg", "image/png", "application/pdf"],
      maxFileSizeMb: 10,
    };
  }

  return undefined;
};

const mapField = (field: PaymentMethodFieldHttp): PaymentMethodField => ({
  id: field.id,
  label: field.label,
  type: mapFieldType(field.type),
  required: field.required,
  placeholder: field.placeholder,
  helperText: field.helper_text,
  pattern: field.pattern,
  options: field.options?.map(mapOption),
});

const mapFieldType = (type: string): PaymentMethodFieldType => {
  const normalized = type.toLowerCase();

  if (normalized === "email" || normalized === "tel" || normalized === "number" || normalized === "select") {
    return normalized;
  }

  return "text";
};

const mapOption = (option: PaymentMethodFieldOptionHttp): PaymentMethodFieldOption => ({
  label: option.label,
  value: option.value,
});
